import { isValid, getYear } from "date-fns";

const MONTH_NAMES: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

interface ParseOptions {
  mode: "date" | "datetime";
  baseDate?: Date;
}

/**
 * Normalize input string: trim and collapse all common delimiters
 * (spaces, dashes, dots, slashes, commas) into a single space so that
 * "feb-5", "feb.5", "feb/5", "feb 5", "2-5-26", "2/5/26", "2 5 26"
 * are all treated identically by downstream patterns.
 */
function normalizeInput(input: string): string {
  return input.trim().replace(/[\s\-./,]+/g, " "); // normalize all delimiters to single space
}

/**
 * Try to extract time from string, returns hours/minutes or null
 */
function extractTime(input: string): { hours: number; minutes: number } | null {
  // Patterns: 8am, 8 am, 8:00am, 8:00 am, 14:00, 2:30pm
  const timePatterns = [
    /(\d{1,2}):(\d{2})\s*(am|pm)?/i, // 8:00, 8:00am, 8:00 pm
    /(\d{1,2})\s*(am|pm)/i, // 8am, 8 pm
  ];

  for (const pattern of timePatterns) {
    const match = input.match(pattern);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2] && !isNaN(parseInt(match[2], 10)) ? parseInt(match[2], 10) : 0;
      const meridiem = (match[3] || match[2])?.toLowerCase();

      // Handle meridiem - check if it's am/pm
      if (meridiem === "pm" && hours < 12) hours += 12;
      if (meridiem === "am" && hours === 12) hours = 0;

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return { hours, minutes };
      }
    }
  }
  return null;
}

/**
 * Remove time portion from input string
 */
function removeTimeFromInput(input: string): string {
  return input
    .replace(/\d{1,2}:\d{2}\s*(am|pm)?/gi, "")
    .replace(/\d{1,2}\s*(am|pm)/gi, "")
    .trim();
}

/**
 * Parse month name to month number (0-indexed)
 */
function parseMonthName(str: string): number | null {
  const lower = str.toLowerCase();
  return MONTH_NAMES[lower] ?? null;
}

/**
 * Expand 2-digit year to 4-digit (24 → 2024)
 */
function expandYear(year: number): number {
  if (year < 100) {
    const currentCentury = Math.floor(getYear(new Date()) / 100) * 100;
    return currentCentury + year;
  }
  return year;
}

/**
 * Try parsing date from various formats
 */
function parseDatePart(input: string, baseDate: Date): Date | null {
  const currentYear = getYear(baseDate);

  // Try: Month Day, Year (Dec 25, 2024 or December 25 2024 or Dec, 25)
  const monthNamePattern = /^([a-z]+)\s*,?\s*(\d{1,2})(?:\s*,?\s*(\d{2,4}))?$/i;
  const monthMatch = input.match(monthNamePattern);
  if (monthMatch) {
    const month = parseMonthName(monthMatch[1]);
    const day = parseInt(monthMatch[2], 10);
    const year = monthMatch[3] ? expandYear(parseInt(monthMatch[3], 10)) : currentYear;

    if (month !== null && day >= 1 && day <= 31) {
      const date = new Date(year, month, day);
      if (isValid(date)) return date;
    }
  }

  // Try: Day Month Year (25 Dec 2024)
  const dayMonthPattern = /^(\d{1,2})\s+([a-z]+)(?:\s*,?\s*(\d{2,4}))?$/i;
  const dayMonthMatch = input.match(dayMonthPattern);
  if (dayMonthMatch) {
    const day = parseInt(dayMonthMatch[1], 10);
    const month = parseMonthName(dayMonthMatch[2]);
    const year = dayMonthMatch[3] ? expandYear(parseInt(dayMonthMatch[3], 10)) : currentYear;

    if (month !== null && day >= 1 && day <= 31) {
      const date = new Date(year, month, day);
      if (isValid(date)) return date;
    }
  }

  // Try: MM DD YYYY or MM DD YY or MM DD (all delimiters normalized to space)
  const numericPattern = /^(\d{1,2})\s(\d{1,2})(?:\s(\d{2,4}))?$/;
  const numericMatch = input.match(numericPattern);
  if (numericMatch) {
    const month = parseInt(numericMatch[1], 10) - 1; // 0-indexed
    const day = parseInt(numericMatch[2], 10);
    const year = numericMatch[3] ? expandYear(parseInt(numericMatch[3], 10)) : currentYear;

    if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
      const date = new Date(year, month, day);
      if (isValid(date)) return date;
    }
  }

  // Try ISO format: YYYY-MM-DD (all delimiters normalized to space)
  const isoPattern = /^(\d{4})\s(\d{1,2})\s(\d{1,2})$/;
  const isoMatch = input.match(isoPattern);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10) - 1;
    const day = parseInt(isoMatch[3], 10);

    const date = new Date(year, month, day);
    if (isValid(date)) return date;
  }

  return null;
}

/**
 * Main parsing function - tries multiple strategies to parse user input.
 * All common delimiters (space, slash, dash, dot, comma) are normalized
 * before matching, so "feb 5", "feb-5", "feb/5", "feb.5" all parse the same.
 *
 * Supported date formats:
 * - Numeric: 12/25/2024, 12-25-2024, 12.25.2024, 12 25 24, 9/13
 * - Month names: Dec 25, feb-5, feb.05, December 25 2024, 25 Dec
 * - ISO: 2024-12-25
 *
 * Time formats (datetime mode):
 * - 8am, 8 am, 8 AM, 8:00am, 8:00 AM
 * - 14:00, 14:30
 * - 8:30pm, 8:30 PM
 */
export function parseFlexibleDate(input: string, options: ParseOptions): Date | null {
  if (!input || !input.trim()) return null;

  const baseDate = options.baseDate || new Date();
  const normalized = normalizeInput(input);

  // Extract time if datetime mode
  let time: { hours: number; minutes: number } | null = null;
  let datePart = normalized;

  if (options.mode === "datetime") {
    time = extractTime(normalized);
    datePart = removeTimeFromInput(normalized);
  }

  // Parse the date portion
  const parsedDate = parseDatePart(datePart, baseDate);
  if (!parsedDate) return null;

  // Apply time if extracted
  if (time) {
    parsedDate.setHours(time.hours, time.minutes, 0, 0);
  } else if (options.mode === "datetime") {
    // Keep current time if no time specified in datetime mode
    parsedDate.setHours(baseDate.getHours(), baseDate.getMinutes(), 0, 0);
  }

  return parsedDate;
}
